import { SpaceBetween, Button } from '@cloudscape-design/components';
import { useState, memo, useEffect } from 'react';
import { QuestionResultType } from 'src/contstant/commonLabels';
import './AuditQuestionActionButton.css';

interface AuditQuestionActionButtonProps {
  selectedAnswer?: string;
  ukey: string;
  onAnswerClick: (result?: QuestionResultType, isAll?: boolean) => void;
  showAll?: boolean;
  isLoading: boolean;
  disable?: boolean;
  disabledReason?: string;
}

// Action buttons for audit questions: Pass, Fail, N/A with loading states
const AuditQuestionActionButton = ({
  ukey,
  selectedAnswer,
  onAnswerClick,
  showAll = false,
  isLoading,
  disable = false,
  disabledReason,
}: AuditQuestionActionButtonProps) => {
  // Track which button was last clicked for loading state
  const [lastClicked, setLastClicked] = useState<
    Record<string, QuestionResultType | undefined>
  >({});

  const handleClick = (result: QuestionResultType, isAll?: boolean) => {
    const finalResult = selectedAnswer === result ? undefined : result;
    setLastClicked({ [ukey]: finalResult });
    onAnswerClick(finalResult, isAll);
  };

  // Clear loading state when operation completes
  useEffect(() => {
    if (!isLoading) setLastClicked({});
  }, [isLoading]);
  return (
    <SpaceBetween key={ukey} direction="horizontal" size="s">
      <Button
        data-testid="pass-button"
        disabled={disable}
        loading={isLoading && lastClicked[ukey] === QuestionResultType.PASS}
        iconName="status-positive"
        variant={selectedAnswer === QuestionResultType.PASS ? 'primary' : 'normal'}
        onClick={() => handleClick(QuestionResultType.PASS, showAll)}
        disabledReason={disabledReason}
      >
        {showAll ? 'Pass All' : 'Pass'}
      </Button>
      <Button
        data-testid="fail-button"
        disabled={disable}
        loading={isLoading && lastClicked[ukey] === QuestionResultType.FAIL}
        iconName="status-negative"
        variant={selectedAnswer === QuestionResultType.FAIL ? 'primary' : 'normal'}
        onClick={() => handleClick(QuestionResultType.FAIL, showAll)}
        disabledReason={disabledReason}
      >
        {showAll ? 'Fail All' : 'Fail'}
      </Button>
      <Button
        data-testid="na-button"
        disabled={disable}
        loading={isLoading && lastClicked[ukey] === QuestionResultType.NA}
        iconName="status-info"
        variant={selectedAnswer === QuestionResultType.NA ? 'primary' : 'normal'}
        onClick={() => handleClick(QuestionResultType.NA, showAll)}
        disabledReason={disabledReason}
      >
        {showAll ? 'N/A All' : 'N/A'}
      </Button>
    </SpaceBetween>
  );
};

// Memoized to prevent unnecessary re-renders
export default memo(AuditQuestionActionButton, (prevProps, nextProps) => {
  return (
    prevProps.selectedAnswer === nextProps.selectedAnswer &&
    prevProps.ukey === nextProps.ukey &&
    prevProps.onAnswerClick === nextProps.onAnswerClick &&
    prevProps.showAll === nextProps.showAll &&
    prevProps.isLoading === nextProps.isLoading
  );
});
